import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import { getLeaderboard, LeaderboardItem } from '../../api/leaderboard';
import Loading from './Loading';
import './TempleModal.css';

interface RankItem {
  id: number;
  address: string;
  score: string;
  colorClass: string;
  bgClass: 'highlight' | 'normal';
}

interface TempleModalProps {
  visible: boolean;
  onClose: () => void;
}

const TempleModal: React.FC<TempleModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const [rankList, setRankList] = useState<RankItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [myRank, setMyRank] = useState<number>(0);
  const [myScore, setMyScore] = useState<string>('');

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  useEffect(() => {
    if (visible) {
      fetchRankData();
    }
  }, [visible]);

  const fetchRankData = async () => {
    setLoading(true);
    try {
      const data = await getLeaderboard(1, 50); // Fetch top 50
      
      setMyRank(data.myRank);
      setMyScore(data.myScore);

      const formattedList: RankItem[] = data.list.map((item) => {
        // Determine color class based on rank
        let colorClass = 'color-green';
        if (item.rank === 1) colorClass = 'color-highlight'; // Gold/Yellow
        else if (item.rank === 2 || item.rank === 3) colorClass = 'color-orange'; // Orange
        else if (item.rank <= 5) colorClass = 'color-cyan'; // Cyan
        
        return {
          id: item.rank, // Use rank as ID since it's unique
          address: item.address,
          score: `${item.score}`, // Add + prefix for display
          colorClass: colorClass,
          bgClass: item.highlight ? 'highlight' : 'normal',
        };
      });

      setRankList(formattedList);
    } catch (error) {
      console.error('Failed to fetch leaderboard', error);
      toast.error(t('common.loadingError', 'Failed to load leaderboard'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (loading) return;
    fetchRankData();
  };

  if (!visible && rankList.length === 0 && !loading) return null;

  return (
    <div className={`temple-modal-container ${visible ? 'visible' : ''}`}>
      <div className="temple-modal-backdrop" style={{ backgroundImage: getImg('popup-backdrop.png') }} onClick={onClose} />

      <div className="temple-modal-content-wrapper">
        <div className="temple-main-frame">
          {/* Background Overlay */}
          <div className="temple-modal-bg-overlay"/>

          {/* Content Panel Box */}
          <div className="temple-modal-panel-bg" style={{ backgroundImage: getImg('tsd_content_panel.png') }} />

          {/* Title */}
          <div className="temple-title-text">{t('temple.title')}</div>
          <div className="temple-subtitle-text">{t('temple.subtitle')}</div>

          {/* Close Button */}
          <div 
            className="temple-close-button" 
            style={{ backgroundImage: getImg('tsd_close_button.png') }} 
            onClick={onClose}
          />

          {/* List Container */}
          <div className="temple-list-container">
            {loading ? (
              <Loading />
            ) : (
              rankList.map((item) => (
                <div 
                  key={item.id} 
                  className="temple-rank-item"
                  style={{ backgroundImage: getImg(item.bgClass === 'highlight' ? 'tsd_rank_item_highlight.png' : 'tsd_rank_item_normal.png') }}
                >
                  <div className="temple-rank-item-container">
                    <div className="temple-rank-icon" style={{ backgroundImage: getImg('tsd_rank_icon.png') }} />
                    <span className="temple-address-text">{item.address}</span>
                    <span className={`temple-score-text ${item.colorClass}`}>{item.score}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Divider Line */}
          <div className="temple-divider-line" style={{ backgroundImage: getImg('tsd_divider_line.png') }} />

          {/* Refresh Button */}
          <div 
            className="temple-refresh-button" 
            style={{ backgroundImage: getImg('tsd_button_primary.png') }}
            onClick={handleRefresh}
          >
            <span className="temple-refresh-text">{t('common.refresh')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TempleModal;
